import styles from './Consumers.module.scss';
import {
    DataTable,
    DataTableColumn,
} from 'components/data/DataTable/DataTable';
import { OpenModalButton } from 'components/button/OpenModalButton/OpenModalButton';
import { useAuth } from 'hooks';
import { useEffect, useState } from 'react';
import { InviteConsumerModal } from './InviteConsumerModal/InviteConsumerModal';
import { PersonAdd } from '@mui/icons-material';
import { getConsumerAppUsers } from 'lib/firebase';
import { lastLoginFilter } from 'components/DataGridCells/data-grid-formatters';
import { GridRowParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { FirebaseUser } from 'types/User/firebaseTypes';

export interface ConsumersProps {
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

/** The Consumers page shows end-users such as homeowners who own tanks that have monitors. */
export const Consumers = ({ viewAsSupplier }: ConsumersProps) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [consumerUsers, setConsumerUsers] = useState<FirebaseUser[]>([]);
    const [refreshConsumersList, setRefreshConsumersList] = useState(false);
    const [totalNumberOfConsumers, setTotalNumberOfConsumers] = useState(0);

    const onRowClick = (params: GridRowParams<any>) => {
        navigate(`/consumer-details/${params.row.uid}`);
    };

    const columns: DataTableColumn[] = [
        {
            field: 'email',
            headerName: 'Email',
            minWidth: 260,
        },
        {
            field: 'name',
            headerName: 'Name',
        },
        {
            field: 'phone',
            headerName: 'Phone',
        },
        {
            field: 'address',
            headerName: 'Address',
        },
        {
            field: 'uid',
            headerName: 'Uid',
            minWidth: 300,
        },
        {
            field: 'lastLoginApp',
            headerName: 'Last Login App',
            minWidth: 150,
            valueFormatter: lastLoginFilter,
        },
        {
            field: 'deviceCount',
            headerName: 'Monitor Count',
        },
        {
            // TODO
            field: 'readonly',
            headerName: 'Read Only',
        },
    ];

    // Get consumer users data.
    useEffect(() => {
        if (
            user?.protected?.supplierId ||
            user?.protected?.supplierId?.toString() === '0'
        ) {
            setLoading(true);
            getConsumerAppUsers(
                viewAsSupplier ?? user?.protected?.supplierId
            ).then((users) => {
                setConsumerUsers(users);
                setLoading(false);
            });
        }
    }, [user?.protected?.supplierId, viewAsSupplier]);

    useEffect(() => {
        if (consumerUsers?.length) {
            setTotalNumberOfConsumers(consumerUsers.length);
        }
    }, [consumerUsers]);

    // Query for consumers  to get updated list after a new consumer has been invited.
    useEffect(() => {
        if (
            (user?.protected?.supplierId ||
                user?.protected?.supplierId?.toString() === '0') &&
            refreshConsumersList
        ) {
            // Make a query every 2000 ms until the newly invited consumer is added to the results.
            const interval = setInterval(() => {
                setLoading(true);
                getConsumerAppUsers(
                    viewAsSupplier ?? user?.protected?.supplierId ?? ''
                ).then((users) => {
                    const newNumberOfConsumers = users.length ?? 0;
                    const found = newNumberOfConsumers > totalNumberOfConsumers;

                    if (found) {
                        setConsumerUsers(users);
                        setLoading(false);
                        setRefreshConsumersList(false);
                        clearInterval(interval);
                    }
                });
            }, 2000);
        }
    }, [
        refreshConsumersList,
        totalNumberOfConsumers,
        user?.protected?.supplierId,
        viewAsSupplier,
    ]);

    return (
        <div data-testid="Consumers" className={styles.container}>
            <DataTable
                id="consumers"
                columns={columns}
                data={consumerUsers}
                loading={loading}
                dataFormatter={(data) => data}
                getRowId={(user: any) => user.uid}
                toolbarComponents={
                    <OpenModalButton
                        icon={<PersonAdd />}
                        modal={(props) => (
                            <InviteConsumerModal
                                onConsumerInvited={setRefreshConsumersList}
                                supplierId={
                                    viewAsSupplier ??
                                    user?.protected?.supplierId ??
                                    ''
                                }
                                {...props}
                            />
                        )}
                    >
                        Invite Consumer
                    </OpenModalButton>
                }
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'email', sort: 'asc' }],
                    },
                }}
                getRowCount={(data) => data?.length ?? 0}
                onRowClick={onRowClick}
            />
        </div>
    );
};
